<template>
  <div id="map-root">
    <b-alert class="elegant-alert" variant="warning" show>A MNA Online jelenleg tesztelés alatt áll, csak oktatási célra használható. Kutatási felhasználás igénye esetén keresse fel az ELTE HTK Néprajztudományi Kutatóintézetet.</b-alert>

    <!-- No Map Page Selected Notice -->
    <div v-if="(!mapPageData || !mapPageData.name) && !gistaAttribute" class="no-map-selected-notice">
        <div class="notice-content">
            <b-icon-info-circle class="notice-icon"></b-icon-info-circle>
            <div class="notice-text">
                <strong>Kérjük, válasszon térképlapot!</strong>
                <p>Kattintson a felső menüsorban található <strong>"Térképlap választása"</strong> gombra a térképadatok megtekintéséhez.</p>
            </div>
        </div>
    </div>

      <!-- Choropleth legend (only shown in GISta thematic mode) -->
      <div v-if="gistaAttribute && gistaStyleColors.length > 0" class="gista-legend">
        <div class="gista-legend-title">{{ gistaCurrentAttributeLabel }}</div>
        <div v-for="(color, i) in gistaStyleColors" :key="'leg' + i" class="gista-legend-row">
          <span class="gista-legend-swatch" :style="{ backgroundColor: color }"></span>
          <span class="gista-legend-range">{{ gistaLegendRange(i) }}</span>
        </div>
        <div class="gista-legend-source">
          Forrás: GISta Hungarorum
        </div>
      </div>
      <div v-if="gistaChoroplethLoading" class="gista-legend">
        <b-spinner small></b-spinner> GISta-adatok betöltése…
      </div>

      <l-map ref="leafletMap" id="map" :zoom="zoom" :center="center" :maxZoom="14">
          <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
          <!-- GISta choropleth: server-rendered polygon tiles via WMS+SLD.
               Placed BEFORE other GISta overlays so it sits under historical
               base layers (counties, rivers, etc) and under MNA markers.
               `tiled: true` routes through GeoServer's GeoWebCache so tiles
               are pre-rasterized on aligned grid boundaries — keeps polygon
               edges crisp at all zoom levels (matches GISta's own setup). -->
          <l-wms-tile-layer
            v-if="gistaAttribute"
            :key="'gista-choropleth-' + gistaAttribute"
            :base-url="gistaApiBase + '/gista/choropleth-wms/' + gistaAttribute"
            :layers="'allum_project:settlements_1910'"
            format="image/png"
            :transparent="true"
            version="1.1.1"
            :options="{ tiled: true }"
            :attribution="gistaAttribution"
          ></l-wms-tile-layer>
          <l-control-layers v-if="gistaLayersReady" position="topright"></l-control-layers>
          <template v-if="gistaLayersReady">
            <template v-for="layer in gistaWmsLayers">
              <l-wms-tile-layer
                v-if="layer.mode === 'wms'"
                :key="layer.name"
                :base-url="gistaApiBase + '/gista/wms'"
                :layers="layer.name"
                :name="layer.label"
                :visible="layer.default_on"
                layer-type="overlay"
                format="image/png"
                :transparent="true"
                version="1.1.1"
                :options="{ tiled: true }"
                :attribution="gistaAttribution"
              ></l-wms-tile-layer>
              <l-geo-json
                v-else
                :key="layer.name"
                :geojson="gistaGeojsonData[layer.name]"
                :name="layer.label"
                :visible="layer.default_on"
                layer-type="overlay"
                :options-style="() => layer.style || {}"
                :attribution="gistaAttribution"
              ></l-geo-json>
            </template>
          </template>
          <l-marker v-for="marker in markers" :key="marker.id" :lat-lng="marker.coords" :icon="marker.icon || icons[marker.attributeValueId]" :options="markerOptions">
            <l-popup>
              <div>
                <b>{{ marker.locationName }}, {{ marker.locationRegion }}</b>
                <br v-if="marker.currentName"/>
                <span v-if="marker.currentName">Jelenleg: {{ marker.currentName }}, {{ marker.country }}</span>
                <br/><br/>
                <div class="attribute-type">
                  <img v-if="marker.mapIconUrl" :src="marker.mapIconUrl" class="map-icon-inline" />
                  <span v-if="marker.answerValue">{{ marker.answerValue }}</span>
                  <span v-else>{{ marker.attributeName }}</span>
                </div>
                <img v-if="marker.attribImageUrl" :src="marker.attribImageUrl" style="width: 100%" />
                <br/>
                <b-button size="sm" variant="primary" class="mt-2" @click="openCollectionModal(marker.locationName, marker.lauCode)">
                  <b-icon-journal-text></b-icon-journal-text> Metaadatok megtekintése
                </b-button>
              </div>
            </l-popup>
          </l-marker>
          <template v-if="markerClusters.length > 0">
            <vue2-leaflet-markercluster v-for="cluster in markerClusters" :key="'c' + cluster.id" :options="clusterOptions">
              <l-marker v-for="marker in cluster.markers" :key="marker.id" :lat-lng="marker.coords" :icon="marker.icon || icons[marker.attributeValueId]" :options="markerOptions">
                <l-popup>
                  <div>
                    <b>{{ marker.locationName }}, {{ marker.locationRegion }}</b>
                    <br v-if="marker.currentName"/>
                    <span v-if="marker.currentName">Jelenleg: {{ marker.currentName }}, {{ marker.country }}</span>
                    <br/><br/>
                    <div class="attribute-type">
                      <img v-if="marker.mapIconUrl" :src="marker.mapIconUrl" class="map-icon-inline" />
                      <span v-if="marker.answerValue">{{ marker.answerValue }}</span>
                      <span v-else>{{ marker.attributeName }}</span>
                    </div>
                    <img v-if="marker.attribImageUrl" :src="marker.attribImageUrl" style="width: 100%" />
                    <br/>
                    <b-button size="sm" variant="primary" class="mt-2" @click="openCollectionModal(marker.locationName, marker.lauCode)">
                      <b-icon-journal-text></b-icon-journal-text> Metaadatok megtekintése
                    </b-button>
                  </div>
                </l-popup>
              </l-marker>
            </vue2-leaflet-markercluster>
          </template>
      </l-map>
      <Sidebar :attributeValues="mapPageData.attribute_values" :value="selectedFeatures" @input="updateSelectedFeatures"/>

      <!-- Collection Modal -->
      <b-modal v-model="showCollectionModal" :title="'Települési metaadatok – ' + selectedSettlement" size="xl" hide-footer class="elegant-collection-modal explorer-modal">
        <b-tabs v-model="activeModalTab" @input="onModalTabChanged">

        <!-- Tab 1: Collection notebooks (Gyűjtőfüzetek) -->
        <b-tab title="Gyűjtőfüzetek" active>
        <div v-if="loadingCollection" class="text-center py-3">
          <b-spinner></b-spinner> Betöltés...
        </div>
        <div v-else-if="collectionData.length > 0">
          <!-- Metadata Section at Top -->
          <div v-if="metadataRecords.length > 0" class="metadata-section-compact">
            <div class="metadata-inline-list">
              <template v-for="group in metadataRecords">
                <template v-for="(item, index) in group.items">
                  <span :key="item.recfields_kod" class="metadata-item">
                    <span v-if="index === 0 || group.items[index - 1].recfields_nev !== item.recfields_nev" class="metadata-label">{{ item.recfields_nev }}:</span>
                    <span class="metadata-value">
                      <span v-if="item.fieldtyp_nev === 'Text'" v-html="item.kd_text"></span>
                      <span v-else-if="item.fieldtyp_nev === 'Értéklista'" v-html="item.katalog_nev"></span>
                      <span v-else-if="item.fieldtyp_nev === 'Település'">{{ formatTelepulesValue(item) }}</span>
                      <span v-else-if="item.fieldtyp_nev === 'Személy'">{{ item.szemely_nev }}</span>
                    </span>
                  </span>
                </template>
              </template>
            </div>
          </div>

          <!-- Search Input -->
          <div class="collection-search-container">
            <div class="search-input-wrapper">
              <b-icon-search class="search-icon"></b-icon-search>
              <input
                v-model="collectionSearchQuery"
                type="text"
                class="collection-search-input"
                placeholder="Keresés kategóriák, mezőnevek és értékek között..."
              />
              <button
                v-if="collectionSearchQuery"
                class="search-clear-btn"
                @click="collectionSearchQuery = ''"
              >
                <b-icon-x></b-icon-x>
              </button>
            </div>
            <div v-if="collectionSearchQuery && searchResultCount !== null" class="search-result-count">
              {{ searchResultCount }} találat
            </div>
          </div>

          <div class="explorer-container">
          <!-- Left Panel: Category Tree -->
          <div class="explorer-left-panel">
            <!-- Category Tree Section -->
            <div class="category-tree-section-header mb-3">
              <h5>Kérdéskategóriák</h5>
            </div>

            <!-- Category View Mode Toggle -->
            <div class="mb-3">
              <b-button-group size="sm">
                <b-button
                  :variant="categoryViewMode === 'relevant' ? 'primary' : 'outline-primary'"
                  @click="categoryViewMode = 'relevant'"
                >
                  Kapcsolódó
                </b-button>
                <b-button
                  :variant="categoryViewMode === 'all' ? 'primary' : 'outline-primary'"
                  @click="categoryViewMode = 'all'"
                >
                  Összes
                </b-button>
              </b-button-group>
            </div>

            <div v-if="groupedCollectionData.length === 0" class="text-muted text-center py-3">
              <span v-if="collectionSearchQuery">Nincs a keresésnek megfelelő kategória</span>
              <span v-else>Nincs kategorizált adat</span>
            </div>
            <div v-else class="category-tree-nav">
              <category-tree-nav-node
                v-for="category in groupedCollectionData"
                :key="category.kod"
                :category="category"
                :collapsed-categories="collapsedCategories"
                :selected-category-kod="selectedCategoryKod"
                :search-filter="collectionSearchQuery ? categoryMatchesSearch : null"
                :level="0"
                @toggle="toggleCategory"
                @select="selectCategory"
              />
            </div>
          </div>

          <!-- Right Panel: Selected Category Content -->
          <div class="explorer-right-panel">
            <div v-if="!selectedCategoryKod" class="no-selection-message">
              <b-icon-arrow-left class="message-icon"></b-icon-arrow-left>
              <div>
                <strong>Válasszon kategóriát</strong>
                <p>Kattintson a bal oldali listában egy kategóriára a tartalmának megtekintéséhez.</p>
              </div>
            </div>
            <div v-else-if="selectedCategoryData">
              <div class="selected-category-header mb-4">
                <h4>{{ selectedCategoryData.nev }}</h4>
                <span class="category-code-badge">{{ selectedCategoryData.kod }}</span>
              </div>

              <!-- Show message if category has children (not a leaf node) -->
              <div v-if="selectedCategoryData.children && selectedCategoryData.children.length > 0" class="no-selection-message">
                <b-icon-folder class="message-icon"></b-icon-folder>
                <div>
                  <strong>Ez egy szülő kategória</strong>
                  <p>Ez a kategória alkategóriákat tartalmaz. Kérjük, válasszon egy alkategóriát a bal oldali fából a mezők megtekintéséhez.</p>
                </div>
              </div>

              <div v-else-if="filteredSelectedCategoryGroups && filteredSelectedCategoryGroups.length > 0">
                <div class="groups-container">
                  <div
                    v-for="group in filteredSelectedCategoryGroups"
                    :key="group.groupKey"
                    class="field-group"
                  >
                    <div v-if="group.groupHeader" class="group-header">
                      <span class="group-title">{{ group.groupHeader }}</span>
                    </div>
                    <div class="fields-container">
                      <div
                        v-for="item in group.items"
                        :key="item.recfields_kod"
                        class="field-card"
                        :class="{ 'search-highlight': collectionSearchQuery && itemMatchesSearch(item) }"
                        :style="{ marginLeft: (item._hierarchyLevel * 24) + 'px' }"
                      >
                        <div class="field-header">
                          <div class="field-label" v-html="collectionSearchQuery ? highlightSearchMatch(item.recfields_nev) : item.recfields_nev">
                          </div>
                          <div class="field-actions">
                            <button
                              class="btn-create-map"
                              @click="createQuestionMap(item)"
                              title="Térkép készítése"
                            >
                              <b-icon-map></b-icon-map>
                            </button>
                            <div class="field-code">
                              {{ item.recfields_kod }}
                            </div>
                          </div>
                        </div>
                        <div class="field-value">
                          <span v-if="item.fieldtyp_nev === 'Text'" v-html="collectionSearchQuery ? highlightSearchMatch(item.kd_text) : item.kd_text"></span>
                          <span v-else-if="item.fieldtyp_nev === 'Értéklista'" v-html="collectionSearchQuery ? highlightSearchMatch(item.katalog_nev) : item.katalog_nev"></span>
                          <span v-else-if="item.fieldtyp_nev === 'Település'" v-html="collectionSearchQuery ? highlightSearchMatch(formatTelepulesValue(item)) : formatTelepulesValue(item)">
                          </span>
                          <span v-else-if="item.fieldtyp_nev === 'Személy'" v-html="collectionSearchQuery ? highlightSearchMatch(item.szemely_nev) : item.szemely_nev">
                          </span>
                          <div v-else-if="item.fieldtyp_nev === 'Csatolt anyag'" class="media-thumbnail-container">
                            <template v-if="item.media">
                              <img
                                :src="getMediaThumbnailUrl(item.media)"
                                :alt="item.media_nev || 'Media'"
                                class="media-thumbnail"
                                @click="openImageViewer(item.media)"
                              />
                              <div class="media-caption">{{ item.media_nev || item.media }}</div>
                            </template>
                            <span v-else class="text-muted">Nincs csatolt fájl</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted text-center py-3">
                <span v-if="collectionSearchQuery">Nincs a keresésnek megfelelő adat ebben a kategóriában</span>
                <span v-else>Nincs elérhető adat ebben a kategóriában</span>
              </div>
            </div>
          </div>
          </div>
        </div>
        <div v-else class="text-muted text-center py-3">
          Nincs elérhető adat
        </div>
        </b-tab>

        <!-- Tab 2: GISta thematic data for this settlement -->
        <b-tab :title="gistaTabTitle">
          <div v-if="!selectedLauCode" class="text-muted text-center py-4">
            Ez a település nincs GISta-megfelelővel összerendelve.
          </div>
          <div v-else-if="loadingGistaSettlement" class="text-center py-3">
            <b-spinner></b-spinner> GISta-adatok betöltése…
          </div>
          <div v-else-if="gistaSettlement">
            <div class="gista-facts-meta">
              <span v-if="gistaSettlement.megye"><strong>Vármegye (1910):</strong> {{ gistaSettlement.megye }}</span>
              <span v-if="gistaSettlement.jaras"><strong>Járás:</strong> {{ gistaSettlement.jaras }}</span>
              <span v-if="gistaSettlement.mai_orszag"><strong>Mai ország:</strong> {{ gistaSettlement.mai_orszag }}</span>
              <a :href="gistaSearchUrl()" target="_blank" rel="noopener" class="gista-facts-link">
                <b-icon-box-arrow-up-right></b-icon-box-arrow-up-right> GISta Hungarorumon
              </a>
            </div>
            <div class="gista-facts-themes">
              <div v-for="theme in gistaSettlement.themes" :key="theme.key" class="gista-facts-theme">
                <h6 class="gista-facts-theme-title">{{ theme.label }}</h6>
                <ul class="gista-facts-list">
                  <li v-for="attr in theme.attributes" :key="attr.key">
                    <span class="gista-facts-label">{{ attr.label }}:</span>
                    <span class="gista-facts-value">{{ formatGistaValue(attr.value) }}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div class="gista-facts-source">
              Forrás: <a href="http://gistahungarorum.abtk.hu" target="_blank" rel="noopener">GISta Hungarorum</a> (HUN-REN BTK), CC BY-NC-SA 4.0
            </div>
          </div>
          <div v-else class="text-muted text-center py-4">
            Nincs elérhető GISta-adat erre a településre.
          </div>
        </b-tab>

        </b-tabs>
      </b-modal>

      <!-- Image Viewer Modal -->
      <b-modal v-model="imageViewerVisible" size="xl" hide-footer centered class="image-viewer-modal" @hidden="closeImageViewer">
        <template #modal-header="{ close }">
          <h5 class="modal-title">Kép megtekintése</h5>
          <button type="button" class="close" @click="close()">
            <span aria-hidden="true">&times;</span>
          </button>
        </template>
        <div class="image-viewer-container">
          <img v-if="imageViewerUrl" :src="imageViewerUrl" alt="Full image" class="full-image" />
        </div>
      </b-modal>
  </div>
</template>

    <script>
    import L from 'leaflet';
    import {LMap, LTileLayer, LMarker, LPopup, LWMSTileLayer as LWmsTileLayer, LGeoJson, LControlLayers} from 'vue2-leaflet';
    import Vue2LeafletMarkercluster from 'vue2-leaflet-markercluster';
    import Sidebar from './Sidebar.vue'
    import CategoryTreeNavNode from './CategoryTreeNavNode.vue'
    import { latLng } from "leaflet";
    import {encode} from "html-entities";
    import {capitalizeFirst} from "../utils";

    export default {
      components: {
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        LWmsTileLayer,
        LGeoJson,
        LControlLayers,
        Vue2LeafletMarkercluster,
        Sidebar,
        CategoryTreeNavNode
      },
      props: [
          'mapPageData',
          'selectedFeatures',
          'questionCode',
          'gistaAttribute'
      ],
      data () {
        return {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution:
            '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
          zoom: 7,
          center: [47.1621, 20.1825], //Szolnok
          icons: {},
          markers: [],
          //filteredMarkers: [],
          markerClusters: [],
          clusterOptions: {
            spiderLegPolylineOptions: { weight: 1.5, color: '#222', opacity: 0.7 },
            spiderfyOnMaxZoom: true
          },
          // keyboard:false stops Leaflet from setting tabindex=0 on each
          // marker. Safari auto-scrolls focused elements into view via
          // scrollIntoViewIfNeeded, which shifts the map pane and steals
          // the click before Leaflet's popup-open handler can run.
          markerOptions: { keyboard: false },
          showCollectionModal: false,
          selectedSettlement: '',
          selectedLauCode: null,
          gistaSettlement: null,  // {found, name_1912, mai_nev, megye, jaras, themes:[...]} or null
          loadingGistaSettlement: false,
          activeModalTab: 0,
          loadingCollection: false,
          collectionData: [],
          collectionFields: [
            { key: 'recfields_kod', label: 'Kód', sortable: true },
            { key: 'recfields_nev', label: 'Mező neve', sortable: true },
            { key: 'kd_value', label: 'Érték', sortable: false }
          ],
          categoryTree: [],
          collapsedCategories: {},
          categoryViewMode: 'relevant', // 'relevant' or 'all'
          mapPageAssociations: [],
          selectedCategoryKod: null, // For explorer-style navigation
          imageViewerVisible: false,
          imageViewerUrl: null,
          collectionSearchQuery: '', // Search query for filtering collections
          gistaApiBase: process.env.VUE_APP_API_URL || 'https://mna_online_backend.test/api',
          gistaAttribution: 'Történeti rétegek: <a target="_blank" href="http://gistahungarorum.abtk.hu">GISta Hungarorum</a> (HUN-REN BTK), <a target="_blank" href="https://creativecommons.org/licenses/by-nc-sa/4.0/">CC BY-NC-SA 4.0</a>',
          gistaWmsLayers: [],
          gistaGeojsonData: {}, // layer name -> parsed GeoJSON
          gistaLayersReady: false,
          // Choropleth state (WS3)
          gistaChoroplethLoading: false,
          gistaStyleCuts: [],          // interior cut points (n_classes - 1 thresholds)
          gistaStyleColors: [],        // hex colors, one per class
          gistaThemes: []              // for label lookups
        };
      },
      computed: {
        gistaTabTitle() {
          if (!this.selectedLauCode) return 'GISta (nincs)';
          if (this.gistaSettlement) {
            const count = (this.gistaSettlement.themes || [])
              .reduce((n, t) => n + (t.attributes ? t.attributes.length : 0), 0);
            return `GISta-adatok (${count})`;
          }
          return 'GISta-adatok';
        },
        gistaCurrentAttributeLabel() {
          if (!this.gistaAttribute) return '';
          for (const theme of this.gistaThemes) {
            for (const attr of (theme.attributes || [])) {
              if (attr.key === this.gistaAttribute) {
                return theme.label + ' — ' + attr.label;
              }
            }
          }
          return this.gistaAttribute;
        },
        normalizedSearchQuery() {
          return this.collectionSearchQuery.toLowerCase().trim();
        },
        searchResultCount() {
          if (!this.normalizedSearchQuery) {
            return null;
          }
          let count = 0;
          const countMatches = (categories) => {
            for (const cat of categories) {
              if (cat.groups) {
                for (const group of cat.groups) {
                  for (const item of group.items) {
                    if (this.itemMatchesSearch(item)) {
                      count++;
                    }
                  }
                }
              }
              if (cat.children && cat.children.length > 0) {
                countMatches(cat.children);
              }
            }
          };
          countMatches(this.groupedCollectionData);
          return count;
        },
        metadataRecords() {
          if (!this.collectionData || this.collectionData.length === 0) {
            return [];
          }
          // Return records where recfields_kod starts with '000' but is not '000.1'
          const records = this.collectionData.filter(item =>
            item.recfields_kod &&
            item.recfields_kod.startsWith('000') &&
            item.recfields_kod !== '000.1'
          );
          return this.organizeFieldsIntoGroups(records);
        },
        selectedCategoryData() {
          if (!this.selectedCategoryKod) {
            return null;
          }

          // Find the selected category in the tree
          const findCategory = (categories, kod) => {
            for (const cat of categories) {
              if (cat.kod === kod) {
                return cat;
              }
              if (cat.children && cat.children.length > 0) {
                const found = findCategory(cat.children, kod);
                if (found) return found;
              }
            }
            return null;
          };

          return findCategory(this.groupedCollectionData, this.selectedCategoryKod);
        },
        filteredSelectedCategoryGroups() {
          if (!this.selectedCategoryData || !this.selectedCategoryData.groups) {
            return [];
          }
          return this.getFilteredGroups(this.selectedCategoryData.groups);
        },
        groupedCollectionData() {
          if (!this.collectionData || this.collectionData.length === 0) {
            return [];
          }

          // Create a map of category codes to their data items
          // Exclude metadata records (those with recfields_kod starting with '000')
          const dataMap = new Map();
          this.collectionData.forEach(item => {
            const code = item.category_katalog_kod;
            // Skip metadata records
            if (code && (!item.recfields_kod || !item.recfields_kod.startsWith('000'))) {
              if (!dataMap.has(code)) {
                dataMap.set(code, []);
              }
              dataMap.get(code).push(item);
            }
          });

          // If no category tree is loaded, create a flat structure
          if (!this.categoryTree || this.categoryTree.length === 0) {
            const flatStructure = [];
            dataMap.forEach((items, kod) => {
              flatStructure.push({
                kod: kod,
                nev: items[0]?.category_katalog_nev || kod,
                items: items,
                children: []
              });
            });
            return flatStructure.sort((a, b) => a.kod.localeCompare(b.kod));
          }

          // Determine which tree to use based on view mode
          let treeToUse = this.categoryTree;

          if (this.categoryViewMode === 'relevant') {
            const topicName = this.getRelevantTopicName();
            if (topicName) {
              treeToUse = this.filterRelevantCategories(this.categoryTree, topicName);
            }
          }

          // Build and filter the tree
          return this.buildTreeWithData(treeToUse, dataMap);
        }
      },
      methods: {
        highlightSearchMatch(text) {
          if (!text || !this.normalizedSearchQuery) {
            return text;
          }
          const textStr = String(text);
          const query = this.normalizedSearchQuery;
          const lowerText = textStr.toLowerCase();
          const index = lowerText.indexOf(query);
          if (index === -1) {
            return textStr;
          }
          const before = textStr.substring(0, index);
          const match = textStr.substring(index, index + query.length);
          const after = textStr.substring(index + query.length);
          return `${before}<mark class="search-match-highlight">${match}</mark>${after}`;
        },
        itemMatchesSearch(item) {
          if (!this.normalizedSearchQuery) {
            return true;
          }
          const query = this.normalizedSearchQuery;

          // Check field name
          const fieldName = (item.recfields_nev || '').toLowerCase();
          const originalName = (item._originalName || '').toLowerCase();
          if (fieldName.includes(query) || originalName.includes(query)) {
            return true;
          }

          // Check field code
          const fieldCode = (item.recfields_kod || '').toLowerCase();
          if (fieldCode.includes(query)) {
            return true;
          }

          // Check field values based on type
          if (item.fieldtyp_nev === 'Text') {
            const textValue = (item.kd_text || '').toLowerCase();
            if (textValue.includes(query)) {
              return true;
            }
          } else if (item.fieldtyp_nev === 'Értéklista') {
            const catalogValue = (item.katalog_nev || '').toLowerCase();
            if (catalogValue.includes(query)) {
              return true;
            }
          } else if (item.fieldtyp_nev === 'Település') {
            const settlementValue = (item.telepules_nev || '').toLowerCase();
            const historicalName = (item.mainev || '').toLowerCase();
            if (settlementValue.includes(query) || historicalName.includes(query)) {
              return true;
            }
          } else if (item.fieldtyp_nev === 'Személy') {
            const personValue = (item.szemely_nev || '').toLowerCase();
            if (personValue.includes(query)) {
              return true;
            }
          }

          return false;
        },
        categoryMatchesSearch(category) {
          if (!this.normalizedSearchQuery) {
            return true;
          }
          const query = this.normalizedSearchQuery;

          // Check category name
          const categoryName = (category.nev || '').toLowerCase();
          if (categoryName.includes(query)) {
            return true;
          }

          // Check if any items in groups match
          if (category.groups) {
            for (const group of category.groups) {
              for (const item of group.items) {
                if (this.itemMatchesSearch(item)) {
                  return true;
                }
              }
            }
          }

          // Check if any children match
          if (category.children && category.children.length > 0) {
            for (const child of category.children) {
              if (this.categoryMatchesSearch(child)) {
                return true;
              }
            }
          }

          return false;
        },
        getFilteredGroups(groups) {
          if (!this.normalizedSearchQuery || !groups) {
            return groups;
          }

          return groups
            .map(group => ({
              ...group,
              items: group.items.filter(item => this.itemMatchesSearch(item))
            }))
            .filter(group => group.items.length > 0);
        },
        updateSelectedFeatures(newValue) {
          this.$emit('update:selectedFeatures', newValue);
        },
        async fetchCategoryTree() {
          try {
            const apiUrl = process.env.VUE_APP_API_URL || 'https://mna_online_backend.test/api';
            const response = await fetch(`${apiUrl}/ethnography/categories/tree`);
            const result = await response.json();

            // Skip the topmost "Atlasz" category and use only its children
            if (result && result.children && Array.isArray(result.children)) {
              this.categoryTree = result.children;
            }
          } catch (error) {
            console.error('Error fetching category tree:', error);
          }
        },
        async fetchMapPageAssociations() {
          try {
            const apiUrl = process.env.VUE_APP_API_URL || 'https://mna_online_backend.test/api';
            const response = await fetch(`${apiUrl}/map/pages-associations`);
            const result = await response.json();

            if (Array.isArray(result)) {
              this.mapPageAssociations = result;
            }
          } catch (error) {
            console.error('Error fetching map page associations:', error);
          }
        },
        async fetchGistaThemes() {
          try {
            const r = await fetch(`${this.gistaApiBase}/gista/themes`);
            const result = await r.json();
            if (Array.isArray(result)) this.gistaThemes = result;
          } catch (err) {
            console.error('GISta themes fetch failed:', err);
          }
        },
        async fetchGistaChoropleth(attribute) {
          if (!attribute) {
            this.gistaStyleCuts = [];
            this.gistaStyleColors = [];
            return;
          }
          this.gistaChoroplethLoading = true;
          try {
            const r = await fetch(`${this.gistaApiBase}/gista/choropleth/${encodeURIComponent(attribute)}`);
            if (!r.ok) {
              console.error('GISta choropleth fetch failed:', r.status);
              this.gistaStyleCuts = [];
              this.gistaStyleColors = [];
              return;
            }
            const data = await r.json();
            const style = data && data.style ? data.style : null;
            if (style) {
              this.gistaStyleCuts = style.cuts || [];
              this.gistaStyleColors = style.colors || [];
            }
          } catch (err) {
            console.error('GISta choropleth fetch failed:', err);
            this.gistaStyleCuts = [];
            this.gistaStyleColors = [];
          } finally {
            this.gistaChoroplethLoading = false;
          }
        },
        gistaLegendRange(i) {
          const cuts = this.gistaStyleCuts;
          const n = this.gistaStyleColors.length;
          if (!n) return '';
          const fmt = (x) => {
            if (typeof x !== 'number' || !isFinite(x)) return String(x);
            if (Math.abs(x) >= 100 || Number.isInteger(x)) return String(Math.round(x));
            return x.toFixed(1);
          };
          if (i === 0) return `< ${fmt(cuts[0])}`;
          if (i === n - 1) return `≥ ${fmt(cuts[n - 2])}`;
          return `${fmt(cuts[i - 1])} – ${fmt(cuts[i])}`;
        },
        async fetchGistaWmsLayers() {
          try {
            const response = await fetch(`${this.gistaApiBase}/gista/wms-layers`);
            const result = await response.json();
            if (!Array.isArray(result)) return;

            this.gistaWmsLayers = result;

            // Pre-fetch GeoJSON for every geojson-mode layer in parallel. WMS
            // layers don't need any preload — they're rendered as tiles on
            // demand by Leaflet.
            const geojsonLayers = result.filter(l => l.mode === 'geojson');
            await Promise.all(geojsonLayers.map(async (layer) => {
              try {
                const r = await fetch(`${this.gistaApiBase}/gista/geojson/${encodeURIComponent(layer.name)}`);
                if (r.ok) {
                  this.$set(this.gistaGeojsonData, layer.name, await r.json());
                }
              } catch (err) {
                console.error(`GISta GeoJSON fetch failed for ${layer.name}:`, err);
              }
            }));
            this.gistaLayersReady = true;
          } catch (error) {
            console.error('Error fetching GISta WMS layers:', error);
          }
        },
        buildTreeWithData(categories, dataMap) {
          if (!categories || !Array.isArray(categories)) {
            return [];
          }

          const result = [];

          for (const category of categories) {
            // Recursively process children first
            const childNodes = category.children
              ? this.buildTreeWithData(category.children, dataMap)
              : [];

            // Get items for this category and organize them into groups
            const items = dataMap.get(category.katalog_kod) || [];
            const organizedGroups = this.organizeFieldsIntoGroups(items);

            // Only include this category if it has items OR has children with items
            if (organizedGroups.length > 0 || childNodes.length > 0) {
              result.push({
                kod: category.katalog_kod,
                nev: category.katalog_nev,
                groups: organizedGroups,
                children: childNodes
              });
            }
          }

          return result;
        },
        getRelevantTopicName() {
          // Find the topic_name for the current map page by matching names
          if (!this.mapPageData || !this.mapPageData.name || !this.mapPageAssociations.length) {
            console.log('Missing data - mapPageData:', this.mapPageData, 'associations:', this.mapPageAssociations);
            return null;
          }

          const mapPageName = this.mapPageData.name;
          console.log('Looking for map page name:', mapPageName);

          const association = this.mapPageAssociations.find(
            assoc => assoc.name === mapPageName
          );

          console.log('Found association:', association);
          const topicName = association ? association.topic_name : null;
          console.log('Resolved topic_name:', topicName);

          return topicName;
        },
        filterRelevantCategories(categories, targetTopicName) {
          if (!targetTopicName) {
            console.log('No target topic name provided, returning all categories');
            return categories;
          }

          console.log('Filtering categories for topic:', targetTopicName);
          console.log('Full category tree:', categories);

          // Helper to find a category and build path to it
          const findCategoryPath = (cats, target, path = []) => {
            for (const cat of cats) {
              const currentPath = [...path, cat];

              console.log('Checking category:', cat.katalog_nev, 'against target:', target);
              if (cat.katalog_nev === target) {
                console.log('Found matching category!');
                return currentPath;
              }

              if (cat.children && cat.children.length > 0) {
                const foundPath = findCategoryPath(cat.children, target, currentPath);
                if (foundPath) {
                  return foundPath;
                }
              }
            }
            return null;
          };

          // Helper to get all descendants of a category
          const getAllDescendants = (category) => {
            const descendants = [category];
            if (category.children && category.children.length > 0) {
              category.children.forEach(child => {
                descendants.push(...getAllDescendants(child));
              });
            }
            return descendants;
          };

          // Find the path to the target category
          const pathToTarget = findCategoryPath(categories, targetTopicName);

          if (!pathToTarget) {
            console.log('Could not find category with name:', targetTopicName, 'returning all categories');
            return categories; // If not found, return all
          }

          console.log('Path to target category:', pathToTarget.map(c => c.katalog_nev));

          // Get the target category (last in path)
          const targetCategory = pathToTarget[pathToTarget.length - 1];

          // Collect all relevant category codes:
          // 1. All ancestors (in the path)
          // 2. The target itself
          // 3. All descendants of the target
          const descendants = getAllDescendants(targetCategory);
          const relevantCodes = new Set([
            ...pathToTarget.map(c => c.katalog_kod),
            ...descendants.map(c => c.katalog_kod)
          ]);

          console.log('Relevant category codes:', Array.from(relevantCodes));

          // Filter the tree to only include relevant categories
          const filterTree = (cats) => {
            return cats.filter(cat => {
              if (relevantCodes.has(cat.katalog_kod)) {
                // Keep this category, but filter its children too
                if (cat.children && cat.children.length > 0) {
                  cat.children = filterTree(cat.children);
                }
                return true;
              }
              return false;
            });
          };

          const filtered = filterTree(JSON.parse(JSON.stringify(categories))); // Deep clone to avoid mutating original
          console.log('Filtered tree:', filtered);
          return filtered;
        },
        toggleCategory(categoryCode) {
          this.$set(this.collapsedCategories, categoryCode, !this.collapsedCategories[categoryCode]);
        },
        initializeCollapsedCategories() {
          const collapsed = {};

          const markCollapsed = (nodes) => {
            for (const node of nodes) {
              collapsed[node.kod] = true;
              if (node.children && node.children.length > 0) {
                markCollapsed(node.children);
              }
            }
          };

          if (this.groupedCollectionData && this.groupedCollectionData.length > 0) {
            markCollapsed(this.groupedCollectionData);
          }

          this.collapsedCategories = collapsed;
        },
        expandAllCategories() {
          const expanded = {};

          const markExpanded = (nodes) => {
            for (const node of nodes) {
              expanded[node.kod] = false; // false = not collapsed = expanded
              if (node.children && node.children.length > 0) {
                markExpanded(node.children);
              }
            }
          };

          if (this.groupedCollectionData && this.groupedCollectionData.length > 0) {
            markExpanded(this.groupedCollectionData);
          }

          this.collapsedCategories = expanded;
        },
        async openCollectionModal(settlementName, lauCode = null) {
          this.selectedSettlement = settlementName;
          this.selectedLauCode = lauCode;
          this.gistaSettlement = null;
          this.activeModalTab = 0;             // always open on Gyűjtőfüzetek
          this.showCollectionModal = true;
          this.loadingCollection = true;
          this.collectionData = [];
          this.selectedCategoryKod = null;
          this.collectionSearchQuery = '';

          // GISta fetch is deferred to onModalTabChanged so we don't pay the
          // round-trip unless the user actually opens the GISta tab.

          try {
            const apiUrl = process.env.VUE_APP_API_URL || 'https://mna_online_backend.test/api';
            // Remove parentheses and their contents from settlement name for API call
            const settlementNameForApi = settlementName.replace(/\s*\(.*?\)\s*/g, '').trim();
            const response = await fetch(`${apiUrl}/ethnography/kartdata/by-rectype?karton_nev=${encodeURIComponent(settlementNameForApi)}`);
            const result = await response.json();

            if (result.data && Array.isArray(result.data)) {
              this.collectionData = result.data.filter(item => item.recfields_kod !== '000.1');
              // Initialize all categories as collapsed after data is loaded
              this.$nextTick(() => {
                this.initializeCollapsedCategories();
              });
            }
          } catch (error) {
            console.error('Error fetching collection data:', error);
            alert('Hiba történt az adatok betöltésekor');
          } finally {
            this.loadingCollection = false;
          }
        },
        onModalTabChanged(index) {
          // Lazily fetch GISta data the first time the GISta tab is shown.
          if (index === 1 && this.selectedLauCode && !this.gistaSettlement && !this.loadingGistaSettlement) {
            this.fetchGistaSettlement(this.selectedLauCode);
          }
        },
        async fetchGistaSettlement(lauCode) {
          this.loadingGistaSettlement = true;
          try {
            const r = await fetch(`${this.gistaApiBase}/gista/settlement/${encodeURIComponent(lauCode)}`);
            const data = await r.json();
            this.gistaSettlement = data && data.found ? data : null;
          } catch (err) {
            console.error('GISta settlement fetch failed:', err);
            this.gistaSettlement = null;
          } finally {
            this.loadingGistaSettlement = false;
          }
        },
        gistaSearchUrl() {
          if (!this.gistaSettlement) return 'http://gistahungarorum.abtk.hu/';
          const name = this.gistaSettlement.name_1912 || this.gistaSettlement.mai_nev || '';
          // The GISta SPA has no per-settlement permalink today; we link to the
          // homepage with name + coords as a hash hint that's ready when the
          // GISta team adds a deep-link parameter.
          const params = new URLSearchParams({
            q: name,
            lat: this.gistaSettlement.lat ?? '',
            lng: this.gistaSettlement.lng ?? '',
          });
          return `http://gistahungarorum.abtk.hu/?${params.toString()}`;
        },
        formatGistaValue(v) {
          // GISta's WFS returns floats with full precision (e.g. 3.48121827411168).
          // Round non-integer numeric values to 3 decimals for legibility; pass
          // strings/nulls/integers through unchanged.
          if (v === null || v === undefined || v === '') return v;
          const n = Number(v);
          if (!Number.isFinite(n)) return v;
          if (Number.isInteger(n)) return n;
          return Math.round(n * 1000) / 1000;
        },
        selectCategory(categoryKod) {
          this.selectedCategoryKod = categoryKod;
        },
        async createQuestionMap(item) {
          // Use the original full field name (before hierarchical truncation)
          const questionTitle = item._originalName || item.recfields_nev;
          const fieldCode = item.recfields_kod;

          // Close the collection modal
          this.showCollectionModal = false;

          try {
            const apiUrl = process.env.VUE_APP_API_URL || 'https://mna_online_backend.test/api';

            // Fetch all answers for this question across all settlements
            const answersUrl = `${apiUrl}/ethnography/kartdata/by-field?recfields_kod=${encodeURIComponent(fieldCode)}`;
            console.log('Fetching question answers from:', answersUrl);
            const answersResponse = await fetch(answersUrl);
            const answersResult = await answersResponse.json();
            console.log('Question answers API response:', answersResult);
            console.log('Answers data length:', answersResult.data ? answersResult.data.length : 0);

            if (!answersResult.data || !Array.isArray(answersResult.data) || answersResult.data.length === 0) {
              console.warn('No answer data returned from API');
              alert('Nincsenek adatok ehhez a kérdéshez');
              return;
            }

            // Fetch location data for all settlements
            const locationsUrl = `${apiUrl}/map/locations`;
            console.log('Fetching location data from:', locationsUrl);
            const locationsResponse = await fetch(locationsUrl);
            const locationsResult = await locationsResponse.json();
            console.log('Locations API response:', locationsResult);

            if (!locationsResult.data || !Array.isArray(locationsResult.data)) {
              console.warn('No location data returned from API');
              alert('Hiba: nem sikerült betölteni a települések koordinátáit');
              return;
            }

            // Create a map of karton_nev to location data
            const locationsMap = {};
            locationsResult.data.forEach(loc => {
              locationsMap[loc.name] = loc;
            });

            // Merge answer data with location data
            const locationValues = answersResult.data
              .filter(answer => {
                const hasLocation = locationsMap[answer.karton_nev];
                if (!hasLocation) {
                  console.warn('No location found for:', answer.karton_nev);
                }
                return hasLocation;
              })
              .map(answer => {
                const location = locationsMap[answer.karton_nev];
                const formattedValue = this.formatAnswerValue(answer);
                console.log('Answer for', answer.karton_nev, ':', formattedValue, 'Raw answer:', answer);
                return {
                  id: answer.karton_id,
                  map_location: {
                    mnaId: location.mnaId || answer.karton_id,
                    latitude: location.latitude,
                    longitude: location.longitude,
                    name: answer.karton_nev,
                    region: location.region || '',
                    currentName: location.currentName || '',
                    country: location.country || 'Magyarország'
                  },
                  // Store the answer value for display
                  answerValue: formattedValue
                };
              });

            console.log('Merged location values:', locationValues.length);
            console.log('Sample location values:', locationValues.slice(0, 5));

            if (locationValues.length === 0) {
              alert('Nem található térképi adat ehhez a kérdéshez');
              return;
            }

            // Create a custom map page object with the question data
            const questionMapPage = {
              name: questionTitle,
              attribute_values: [{
                mnaId: 'question_' + fieldCode,
                name: questionTitle,
                show: true,
                mapIconUrl: null,
                attribImageUrl: null,
                location_values: locationValues
              }]
            };

            console.log('Created question map page:', questionMapPage);
            console.log('Number of locations:', questionMapPage.attribute_values[0].location_values.length);

            // Update the map with question-specific data
            this.$emit('update:mapPageData', questionMapPage);
            this.$emit('update:selectedFeatures', ['question_' + fieldCode]);
            this.$emit('update:questionCode', fieldCode);
            this.$emit('update:questionTitle', questionTitle);
          } catch (error) {
            console.error('Error creating question map:', error);
            console.error('Error details:', error.message, error.stack);
            alert(`Hiba történt a térkép létrehozásakor: ${error.message}`);
          }
        },
        async loadQuestionMapFromCode(fieldCode) {
          // Load a question map from URL parameter
          // We need to fetch the field name from the API first
          try {
            const apiUrl = process.env.VUE_APP_API_URL || 'https://mna_online_backend.test/api';

            // We'll use the answers endpoint which should include the field name
            const answersUrl = `${apiUrl}/ethnography/kartdata/by-field?recfields_kod=${encodeURIComponent(fieldCode)}`;
            const answersResponse = await fetch(answersUrl);
            const answersResult = await answersResponse.json();

            if (!answersResult.data || !Array.isArray(answersResult.data) || answersResult.data.length === 0) {
              console.warn('No answer data for question code:', fieldCode);
              return;
            }

            // Get the field name from the first answer (they all have the same field name)
            const questionTitle = answersResult.data[0].recfields_nev || `Kérdés: ${fieldCode}`;

            // Create a mock item object to pass to createQuestionMap
            const mockItem = {
              recfields_kod: fieldCode,
              recfields_nev: questionTitle,
              _originalName: questionTitle
            };

            // Call createQuestionMap with the mock item
            await this.createQuestionMap(mockItem);
          } catch (error) {
            console.error('Error loading question map from code:', error);
          }
        },
        formatAnswerValue(answer) {
          // Format the answer based on field type
          if (answer.fieldtyp_nev === 'Text') {
            return answer.kd_text || '';
          } else if (answer.fieldtyp_nev === 'Értéklista') {
            return answer.katalog_nev || '';
          } else if (answer.fieldtyp_nev === 'Település') {
            return answer.telepules_nev ?
              (answer.mainev ? `${answer.telepules_nev} (${answer.mainev})` : answer.telepules_nev) : '';
          } else if (answer.fieldtyp_nev === 'Személy') {
            return answer.szemely_nev || '';
          }
          return '';
        },
        viewPerson(personId) {
          // TODO: Implement person record viewing
          // This could open another modal or navigate to a person detail page
          console.log('View person:', personId);
          alert(`Személy megtekintése: ${personId}`);
        },
        getMediaUrl(filename) {
          const baseUrl = process.env.VUE_APP_MEDIA_BASE_URL || '/media';
          return `${baseUrl}/${filename}`;
        },
        getMediaThumbnailUrl(mediaId) {
          const baseUrl = process.env.VUE_APP_MEDIA_BASE_URL || 'https://ethnography.abtk.hu';
          return `${baseUrl}/kiskepmutat.php?media=${mediaId}`;
        },
        getMediaFullUrl(mediaId) {
          const baseUrl = process.env.VUE_APP_MEDIA_BASE_URL || 'https://ethnography.abtk.hu';
          return `${baseUrl}/filemutat.php?media=${mediaId}`;
        },
        openImageViewer(mediaId) {
          this.imageViewerUrl = this.getMediaFullUrl(mediaId);
          this.imageViewerVisible = true;
        },
        closeImageViewer() {
          this.imageViewerVisible = false;
          this.imageViewerUrl = null;
        },
        formatTelepulesValue(item) {
          if (!item.telepules_nev) {
            return '';
          }
          if (item.mainev) {
            return `${item.telepules_nev} (${item.mainev})`;
          }
          return item.telepules_nev;
        },
        organizeFieldsIntoGroups(items) {
          if (!items || items.length === 0) {
            return [];
          }

          // Sort items by their code
          const sortedItems = [...items].sort((a, b) => {
            const codeA = a.recfields_kod || '';
            const codeB = b.recfields_kod || '';
            return codeA.localeCompare(codeB, undefined, { numeric: true });
          });

          // Group items by their second-level code (e.g., 006.4, 006.5, etc.)
          const groups = {};

          sortedItems.forEach(item => {
            const code = item.recfields_kod || '';
            const parts = code.split('.');

            // Get the group key (first two parts, e.g., "006.4")
            const groupKey = parts.length >= 2 ? `${parts[0]}.${parts[1]}` : code;

            if (!groups[groupKey]) {
              groups[groupKey] = {
                groupKey: groupKey,
                groupHeader: null,
                items: []
              };
            }

            // Temporarily store with absolute level
            groups[groupKey].items.push({
              ...item,
              _absoluteLevel: parts.length,
              _originalName: item.recfields_nev
            });
          });

          // Process each group
          Object.values(groups).forEach(group => {
            // Find the minimum level in this group
            const minLevel = Math.min(...group.items.map(item => item._absoluteLevel));

            // Get top-level items (those at minLevel)
            const topLevelItems = group.items.filter(item => item._absoluteLevel === minLevel);

            // Case 1: Check if all top-level items share a prefix before " - "
            if (topLevelItems.length > 1) {
              const prefixes = topLevelItems
                .map(item => {
                  const name = item._originalName || '';
                  const dashIndex = name.indexOf(' - ');
                  return dashIndex > 0 ? name.substring(0, dashIndex) : null;
                })
                .filter(p => p !== null);

              // If all top-level items have the same prefix, use it as group header
              if (prefixes.length === topLevelItems.length &&
                  prefixes.every(p => p === prefixes[0])) {
                group.groupHeader = prefixes[0];

                // Remove the prefix from top-level items
                topLevelItems.forEach(item => {
                  const name = item._originalName || '';
                  const dashIndex = name.indexOf(' - ');
                  if (dashIndex > 0) {
                    item.recfields_nev = name.substring(dashIndex + 3).trim();
                  }
                });
              }
            }

            // Case 2: Remove parent text from sub-questions
            group.items.forEach(item => {
              if (item._absoluteLevel > minLevel) {
                // This is a sub-question, find its parent
                const itemParts = (item.recfields_kod || '').split('.');
                const parentCode = itemParts.slice(0, -1).join('.');
                const parent = group.items.find(p => p.recfields_kod === parentCode);

                if (parent) {
                  const parentName = parent._originalName || '';
                  const itemName = item._originalName || '';

                  // Check if item name starts with parent name followed by " - "
                  if (itemName.startsWith(parentName + ' - ')) {
                    item.recfields_nev = itemName.substring(parentName.length + 3).trim();
                  }
                }
              }
            });

            // Adjust hierarchy levels relative to the minimum
            group.items = group.items.map(item => ({
              ...item,
              _hierarchyLevel: item._absoluteLevel - minLevel
            }));
          });

          // Convert to array and sort by group key
          return Object.values(groups).sort((a, b) =>
            a.groupKey.localeCompare(b.groupKey, undefined, { numeric: true })
          );
        },
        processMapPageData(mapPageData) {
      let markersBeforeClustering = [];
              this.markers = [];
              this.markerClusters = [];
              mapPageData.attribute_values.forEach(attributeValue => {
                if (attributeValue.show) {
                  attributeValue.location_values.forEach(locationValue => {
                    if (locationValue.map_location.latitude && locationValue.map_location.longitude) {
                      // Create icon for each location individually
                      let icon;
                      if (attributeValue.mapIconUrl) {
                        // Regular map pages use icon images
                        icon = L.icon({
                              iconUrl: attributeValue.mapIconUrl,
                              iconSize: [16, 16],
                              iconAnchor: [8, 20]
                          });
                      } else if (locationValue.answerValue) {
                        // Question maps use text labels
                        // Escape HTML to prevent XSS
                        const escapedValue = locationValue.answerValue
                          .replace(/&/g, '&amp;')
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;')
                          .replace(/"/g, '&quot;')
                          .replace(/'/g, '&#039;');

                        console.log('Creating icon for location:', locationValue.map_location.name, 'with value:', escapedValue);

                        icon = L.divIcon({
                          className: 'answer-label-marker',
                          html: `<div class="answer-label">${escapedValue}</div>`,
                          iconSize: null,  // Let the div size itself
                          iconAnchor: null  // Will be centered via CSS
                        });
                      } else {
                        // Fallback to default icon
                        icon = new L.Icon.Default();
                      }

                      const markerId = locationValue.id + '_' + attributeValue.mnaId;

                      // Store icon in the global icons object
                      this.icons[markerId] = icon;

                      markersBeforeClustering.push({
                        id: locationValue.id,
                        mapLocationId: locationValue.map_location.mnaId,
                        attributeValueId: markerId,  // Use unique ID for each marker
                        icon: icon,  // Store icon directly on marker object
                        coords: latLng(locationValue.map_location.latitude, locationValue.map_location.longitude),
                        locationName: locationValue.map_location.name,
                        locationRegion: locationValue.map_location.region,
                        currentName: locationValue.map_location.currentName,
                        country: locationValue.map_location.country,
                        lauCode: locationValue.map_location.lau_code || null,
                        attributeName: capitalizeFirst(encode(attributeValue.name)),
                        attribImageUrl: attributeValue.attribImageUrl,
                        mapIconUrl: attributeValue.mapIconUrl,
                        answerValue: locationValue.answerValue || null
                      })
                    }
                  });
                }
              });

            function clusterMarkers(markersToCluster) {
              let markerClusters = [];
              let markerIdsToDelete = [];
              let clusterId = 0;
              for (let i = 0; i <= markersToCluster.length-1; i++) {
                if (!markerIdsToDelete.includes(markersToCluster[i].id)) {
                  const currentMarker = markersToCluster[i];
                  const markersAtThisLocation = getMarkersWithCoords(markersToCluster, currentMarker.coords);
                  if (markersAtThisLocation.length > 1) {
                    markerClusters.push({
                      id: clusterId,
                      markers: markersAtThisLocation
                    });
                    clusterId++;
                    for (let marker of markersAtThisLocation) {
                      if (!markerIdsToDelete.includes(marker.id)) {
                        markerIdsToDelete.push(marker.id);
                      }
                    }
                  }
                }
              }
              let markers = markersToCluster.filter((ele) => {
                return !markerIdsToDelete.includes(ele.id);
              });
              return {markerClusters, markers};
            }

            function getMarkersWithCoords(markers, coords) {
              let result = [];
              for (let i = 0; i < markers.length; i++) {
                if (markers[i].coords.lat === coords.lat && markers[i].coords.lng === coords.lng) {
                  result.push(markers[i]);
                }
              }
              return result;
            }

            const clusterResult = clusterMarkers(markersBeforeClustering);
            this.markerClusters = clusterResult.markerClusters;
            this.markers = clusterResult.markers
    }
      },
      watch: {
          markers: function(markers) {
            console.log(markers);
          },
        selectedFeatures: function(selectedFeatures) {
          //console.log(marker.attributeValueId)
          console.log(selectedFeatures)
          this.mapPageData.attribute_values = this.mapPageData.attribute_values.map(attributeValue => {
            if (selectedFeatures.includes(attributeValue.mnaId)) {
              attributeValue.show = true
            } else {
              attributeValue.show = false
            }
            return attributeValue
            //attributeValue == 1
            //return true
          })
          this.processMapPageData(this.mapPageData)
          //this.filteredMarkers = this.markers.filter((marker) => {
            //console.log(marker.attributeValueId)
            //console.log(marker.attributeValueId in selectedFeatures)
            //return selectedFeatures.includes(marker.attributeValueId)
          //})
          console.log(this.filteredMarkers)
        },
          mapPageData: function (mapPageData) {
            this.selectedFeatures = mapPageData.attribute_values.map(attributeValue => attributeValue.mnaId) // select all features by default
            this.processMapPageData(mapPageData)
          },
          categoryViewMode: function() {
            // Reinitialize collapsed state when switching between relevant/all views
            this.$nextTick(() => {
              this.initializeCollapsedCategories();
            });
          },
          collectionSearchQuery: function(newQuery, oldQuery) {
            // When search is initiated, expand all categories to show results
            if (newQuery && !oldQuery) {
              this.expandAllCategories();
            }
            // When search is cleared, collapse all categories
            if (!newQuery && oldQuery) {
              this.initializeCollapsedCategories();
            }
          },
          gistaAttribute: function(newAttr) {
            this.fetchGistaChoropleth(newAttr);
          },
          questionCode: function(newQuestionCode) {
            // When questionCode prop changes (from URL), load the question map
            if (newQuestionCode) {
              this.loadQuestionMapFromCode(newQuestionCode);
            }
          }
      },
      mounted() {
        // Safari on macOS fires two click events per physical click. Leaflet's
        // default marker click is a toggle — open if closed, close if open —
        // so the second synthetic click immediately closes the just-opened
        // popup. Patch _openPopup on Layer.prototype to ignore the close-
        // toggle path within 300ms of an open. One-time, applies to all
        // markers globally for the lifetime of the SPA.
        if (!L.Layer.prototype.__popupToggleDebounced) {
          const origOpenPopup = L.Layer.prototype._openPopup;
          let lastOpenAt = 0;
          L.Layer.prototype._openPopup = function (e) {
            if (this._map && this._popup && this._map.hasLayer(this._popup)
                && this._popup._source === (e.target)) {
              if (Date.now() - lastOpenAt < 300) {
                L.DomEvent.stop(e);
                return;
              }
            } else {
              lastOpenAt = Date.now();
            }
            return origOpenPopup.call(this, e);
          };
          L.Layer.prototype.__popupToggleDebounced = true;
        }

        // Fetch category tree and map page associations when component is mounted
        this.fetchCategoryTree();
        this.fetchMapPageAssociations();
        this.fetchGistaWmsLayers();
        this.fetchGistaThemes();

        // If a gistaAttribute is provided via prop (URL-restored), load it
        if (this.gistaAttribute) {
          this.fetchGistaChoropleth(this.gistaAttribute);
        }

        // The flex layout settles after Vue's mounted hook. Without this,
        // Leaflet caches a stale container size and popups end up off-screen.
        this.$nextTick(() => {
          if (this.$refs.leafletMap && this.$refs.leafletMap.mapObject) {
            this.$refs.leafletMap.mapObject.invalidateSize();
          }
        });

        // If a question code is provided via prop, load it
        if (this.questionCode) {
          this.loadQuestionMapFromCode(this.questionCode);
        }
      }
    }
    </script>

    <style>
    #map-root {
        height: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
    }

    #map {
        flex: 1 1 auto;
        min-height: 0;
        position: relative;
    }

    /* GISta facts panel inside the collection modal */
    .gista-facts-panel {
        margin: 0 0 1rem 0;
        border: 1px solid #d9d4cb;
        border-radius: 0.5rem;
        background: #faf9f7;
        padding: 0.75rem 1rem;
    }
    .gista-facts-panel > summary {
        cursor: pointer;
        font-weight: 600;
        color: #0f766e;
        list-style: none;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9375rem;
    }
    .gista-facts-panel > summary::-webkit-details-marker { display: none; }
    .gista-facts-link {
        margin-left: auto;
        font-size: 0.8125rem;
        font-weight: 500;
        color: #0f766e;
        text-decoration: none;
    }
    .gista-facts-link:hover { text-decoration: underline; }
    .gista-facts-meta {
        margin-top: 0.5rem;
        font-size: 0.8125rem;
        color: #475569;
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem 1.25rem;
    }
    .gista-facts-themes {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 0.5rem 1rem;
        margin-top: 0.75rem;
    }
    .gista-facts-theme-title {
        font-size: 0.8125rem;
        font-weight: 600;
        color: #1e293b;
        margin-bottom: 0.25rem;
        padding-bottom: 0.125rem;
        border-bottom: 1px solid #e5e7eb;
    }
    .gista-facts-list {
        list-style: none;
        padding: 0;
        margin: 0;
        font-size: 0.8125rem;
        line-height: 1.5;
    }
    .gista-facts-label {
        color: #475569;
    }
    .gista-facts-value {
        color: #0f172a;
        font-weight: 500;
        margin-left: 0.5rem;
    }
    .gista-facts-source {
        margin-top: 0.75rem;
        padding-top: 0.5rem;
        border-top: 1px solid #e5e7eb;
        font-size: 0.6875rem;
        color: #94a3b8;
        font-style: italic;
    }

    /* GISta thematic-map legend.
       Bottom-left corner of the map: clears the top-left zoom controls and the
       top-right Leaflet layer picker. Leaflet's attribution sits bottom-right,
       so bottom-left is the only free corner. */
    .gista-legend {
        position: absolute;
        bottom: 1.25rem;
        left: 1rem;
        z-index: 1000;
        background: white;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        box-shadow: 0 2px 12px rgba(0,0,0,0.15);
        font-size: 0.8125rem;
        line-height: 1.4;
        max-width: 320px;
    }
    .gista-legend-title {
        font-weight: 600;
        color: #0f172a;
        margin-bottom: 0.5rem;
        border-bottom: 1px solid #e5e7eb;
        padding-bottom: 0.375rem;
    }
    .gista-legend-row {
        display: flex;
        align-items: center;
        margin: 0.125rem 0;
    }
    .gista-legend-swatch {
        display: inline-block;
        width: 18px;
        height: 12px;
        margin-right: 0.5rem;
        border: 1px solid rgba(0,0,0,0.15);
    }
    .gista-legend-range {
        color: #475569;
    }
    .gista-legend-source {
        margin-top: 0.5rem;
        padding-top: 0.375rem;
        border-top: 1px solid #e5e7eb;
        font-size: 0.6875rem;
        color: #94a3b8;
        font-style: italic;
    }
    /* Elegant Alert Styling */
    .elegant-alert {
        width: 100%;
        margin: 0;
        border: none;
        border-radius: 0;
        background-color: #fef3c7;
        color: #78350f;
        padding: 0.75rem 1.5rem;
        font-weight: 500;
        text-align: center;
        font-size: 0.9375rem;
        border-bottom: 1px solid #fde68a;
    }

    /* No Map Page Selected Notice */
    .no-map-selected-notice {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1000;
        width: 90%;
        max-width: 600px;
    }

    .notice-content {
        background: white;
        border: 2px solid #0f766e;
        border-radius: 0.75rem;
        padding: 2rem;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        display: flex;
        align-items: flex-start;
        gap: 1.5rem;
    }

    .notice-icon {
        color: #0f766e;
        font-size: 2.5rem;
        flex-shrink: 0;
        margin-top: 0.25rem;
    }

    .notice-text {
        flex: 1;
    }

    .notice-text strong {
        display: block;
        color: #0f766e;
        font-size: 1.25rem;
        margin-bottom: 0.75rem;
        font-weight: 600;
    }

    .notice-text p {
        color: #475569;
        font-size: 0.9375rem;
        line-height: 1.6;
        margin: 0;
    }

    @media (max-width: 768px) {
        .no-map-selected-notice {
            width: 95%;
        }

        .notice-content {
            padding: 1.5rem;
            gap: 1rem;
            flex-direction: column;
            align-items: center;
            text-align: center;
        }

        .notice-icon {
            font-size: 2rem;
            margin-top: 0;
        }
    }

    /* Elegant Leaflet Popup Styling */
    .leaflet-popup-content-wrapper {
        border-radius: 0.5rem;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        padding: 0;
        overflow: hidden;
    }

    .leaflet-popup-content {
        margin: 0;
        padding: 1rem 1.25rem;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue',
          'Arial', sans-serif;
        line-height: 1.6;
    }

    .leaflet-popup-content b {
        color: #0f172a;
        font-size: 1rem;
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 600;
    }

    .leaflet-popup-content span {
        color: #475569;
        line-height: 1.65;
    }

    .leaflet-popup-content .attribute-type {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }

    .leaflet-popup-content .map-icon-inline {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
    }

    .leaflet-popup-content .btn {
        margin-top: 0.75rem;
        border-radius: 0.375rem;
        font-weight: 500;
        transition: all 0.25s ease;
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
    }

    .leaflet-popup-content .btn svg {
        flex-shrink: 0;
    }

    .leaflet-popup-content .btn-primary {
        background-color: #0f766e;
        border-color: #0f766e;
    }

    .leaflet-popup-content .btn-primary:hover {
        background-color: #115e59;
        border-color: #115e59;
    }

    .leaflet-popup-tip {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    /* Elegant Collection Modal Styling */
    .elegant-collection-modal .modal-header {
        background: #faf9f7;
        color: #0f766e;
        padding: 1.5rem 1.75rem;
        border-bottom: 1px solid #e2e8f0;
    }

    .elegant-collection-modal .modal-title {
        font-weight: 600;
        color: #0f766e;
        letter-spacing: -0.01em;
    }

    .elegant-collection-modal .modal-body {
        padding: 0;
        background: #f8fafc;
        max-height: 70vh;
        overflow: hidden;
    }

    /* Metadata Section at Top */
    /* Compact Horizontal Metadata */
    .metadata-section-compact {
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
        padding: 0.75rem 1.5rem;
    }

    .metadata-inline-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
        align-items: baseline;
        line-height: 1.6;
    }

    .metadata-item {
        display: inline-flex;
        align-items: baseline;
        gap: 0.25rem;
    }

    .metadata-label {
        font-size: 0.8125rem;
        font-weight: 600;
        color: #6b7280;
    }

    .metadata-value {
        font-size: 0.875rem;
        color: #111827;
    }

    .metadata-value::after {
        content: ", ";
        color: #9ca3af;
    }

    .metadata-item:last-child .metadata-value::after {
        content: "";
    }

    /* Collection Search Styles */
    .collection-search-container {
        padding: 0.75rem 1.5rem;
        background: #f9fafb;
        border-bottom: 1px solid #e5e7eb;
    }

    .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
    }

    .search-icon {
        position: absolute;
        left: 0.875rem;
        color: #9ca3af;
        font-size: 1rem;
        pointer-events: none;
    }

    .collection-search-input {
        width: 100%;
        padding: 0.625rem 2.5rem 0.625rem 2.5rem;
        font-size: 0.9375rem;
        border: 1.5px solid #e5e7eb;
        border-radius: 0.5rem;
        background: white;
        color: #111827;
        transition: all 0.2s ease;
    }

    .collection-search-input:focus {
        outline: none;
        border-color: #0f766e;
        box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.1);
    }

    .collection-search-input::placeholder {
        color: #9ca3af;
    }

    .search-clear-btn {
        position: absolute;
        right: 0.5rem;
        background: transparent;
        border: none;
        color: #9ca3af;
        cursor: pointer;
        padding: 0.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.25rem;
        transition: all 0.15s ease;
    }

    .search-clear-btn:hover {
        color: #374151;
        background: #f3f4f6;
    }

    .search-result-count {
        margin-top: 0.5rem;
        font-size: 0.8125rem;
        color: #6b7280;
    }

    /* Explorer Container */
    .explorer-container {
        display: flex;
        height: calc(70vh - 150px);
        overflow: hidden;
    }

    /* Left Panel */
    .explorer-left-panel {
        width: 320px;
        flex-shrink: 0;
        background: white;
        border-right: 1px solid #e2e8f0;
        overflow-y: auto;
        padding: 1.5rem;
    }

    /* Right Panel */
    .explorer-right-panel {
        flex: 1;
        overflow-y: auto;
        padding: 1.5rem;
        background: #f8fafc;
    }

    /* Category Tree Navigation */
    .category-tree-nav {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .category-nav-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem;
        background: white;
        border: 1px solid #e7e5e4;
        border-radius: 0.5rem;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .category-nav-item:hover {
        background: #f8fafc;
        border-color: #0f766e;
        transform: translateX(4px);
    }

    .category-nav-item.active {
        background: #f0fdfa;
        border-color: #0f766e;
        border-left-width: 4px;
    }

    .category-nav-name {
        font-size: 0.9375rem;
        font-weight: 500;
        color: #0f172a;
        flex: 1;
    }

    .category-nav-item.active .category-nav-name {
        color: #0f766e;
        font-weight: 600;
    }

    .category-nav-icon {
        color: #cbd5e1;
        font-size: 0.875rem;
        transition: all 0.2s ease;
    }

    .category-nav-item:hover .category-nav-icon,
    .category-nav-item.active .category-nav-icon {
        color: #0f766e;
    }

    /* No Selection Message */
    .no-selection-message {
        display: flex;
        align-items: center;
        gap: 1.5rem;
        padding: 3rem 2rem;
        background: white;
        border: 2px dashed #cbd5e1;
        border-radius: 0.75rem;
        color: #64748b;
        text-align: left;
    }

    .no-selection-message .message-icon {
        font-size: 3rem;
        color: #cbd5e1;
        flex-shrink: 0;
    }

    .no-selection-message strong {
        display: block;
        color: #475569;
        font-size: 1.125rem;
        margin-bottom: 0.5rem;
    }

    .no-selection-message p {
        margin: 0;
        line-height: 1.6;
    }

    /* Selected Category Header */
    .selected-category-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding-bottom: 1rem;
        border-bottom: 2px solid #e7e5e4;
    }

    .selected-category-header h4 {
        color: #0f766e;
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0;
        letter-spacing: -0.02em;
    }

    .category-code-badge {
        font-size: 0.875rem;
        font-weight: 600;
        color: #64748b;
        background: #f1f5f9;
        padding: 0.375rem 0.75rem;
        border-radius: 0.375rem;
        font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
    }

    .elegant-collection-modal .btn-group .btn {
        border-radius: 0.375rem;
        font-weight: 500;
        padding: 0.5rem 1rem;
        transition: all 0.25s ease;
        font-size: 0.9375rem;
    }

    .elegant-collection-modal .btn-group .btn:first-child {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }

    .elegant-collection-modal .btn-group .btn:last-child {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    .elegant-collection-modal .btn-primary {
        background-color: #0f766e;
        border-color: #0f766e;
        color: white;
    }

    .elegant-collection-modal .btn-primary:hover {
        background-color: #115e59;
        border-color: #115e59;
    }

    .elegant-collection-modal .btn-outline-primary {
        border: 1.5px solid #0f766e;
        color: #0f766e;
        background: white;
    }

    .elegant-collection-modal .btn-outline-primary:hover {
        background: #0f766e;
        color: white;
        border-color: #0f766e;
    }

    .elegant-collection-modal .spinner-border {
        color: #0f766e;
    }

    /* Refined Map Controls */
    .leaflet-control-zoom a {
        transition: all 0.25s ease;
    }

    .leaflet-control-zoom a:hover {
        background: #0f766e;
        color: white;
    }

    .leaflet-bar {
        border: none;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        border-radius: 0.375rem;
        overflow: hidden;
    }

    .leaflet-bar a {
        border-bottom: 1px solid #e2e8f0;
    }

    .leaflet-bar a:first-child {
        border-top-left-radius: 0.375rem;
        border-top-right-radius: 0.375rem;
    }

    .leaflet-bar a:last-child {
        border-bottom-left-radius: 0.375rem;
        border-bottom-right-radius: 0.375rem;
        border-bottom: none;
    }

    /* Subtle Map Marker Enhancement */
    .leaflet-marker-icon {
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
    }

    .leaflet-marker-icon:hover {
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
        cursor: pointer;
    }

    /* Marker Cluster Styling Enhancement */
    .marker-cluster-small,
    .marker-cluster-medium,
    .marker-cluster-large {
        background-color: rgba(15, 118, 110, 0.6) !important;
    }

    .marker-cluster-small div,
    .marker-cluster-medium div,
    .marker-cluster-large div {
        background-color: rgba(15, 118, 110, 0.8) !important;
        color: white;
        font-weight: 600;
    }


    /* Category Tree Section Header Styling */
    .category-tree-section-header {
        border-bottom: 2px solid #0f766e;
        padding-bottom: 0.5rem;
    }

    .category-tree-section-header h5 {
        color: #0f766e;
        font-size: 1.25rem;
        font-weight: 700;
        margin: 0;
        letter-spacing: -0.02em;
    }

    /* Groups Container - Clean and minimal */
    .groups-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    /* Field Group - Modern container style */
    .field-group {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 0.75rem;
        padding: 1.25rem;
        margin-bottom: 0;
    }

    /* Group Header - Simple and clean */
    .group-header {
        margin-bottom: 1rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid #f3f4f6;
    }

    .group-title {
        font-size: 0.875rem;
        font-weight: 600;
        color: #6b7280;
        letter-spacing: 0.025em;
        text-transform: uppercase;
    }

    /* Field Cards Container */
    .fields-container {
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    /* Individual Field Card - Clean list style */
    .field-card {
        background: transparent;
        border: none;
        border-bottom: 1px solid #f3f4f6;
        border-radius: 0;
        padding: 1rem 0;
        margin-bottom: 0;
        box-shadow: none;
    }

    .field-card:last-child {
        border-bottom: none;
    }

    .field-card.search-highlight {
        background: #fef9c3;
        border-radius: 0.375rem;
        padding: 1rem 0.75rem;
        margin-left: 0 !important;
        border-left: 3px solid #eab308;
    }

    /* Search match highlight within text */
    .search-match-highlight {
        background-color: #fbbf24;
        color: #1f2937;
        padding: 0 0.125rem;
        border-radius: 0.125rem;
    }


    /* Field Header (contains label, actions and code) */
    .field-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
        margin-bottom: 0.375rem;
    }

    .field-actions {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-shrink: 0;
    }

    /* Create Map Button - Minimal icon style */
    .btn-create-map {
        background: transparent;
        border: none;
        color: #0f766e;
        padding: 0.25rem;
        border-radius: 0.25rem;
        font-size: 0.875rem;
        cursor: pointer;
        transition: all 0.15s ease;
        display: flex;
        align-items: center;
        gap: 0.25rem;
        font-weight: 500;
        opacity: 0.7;
    }

    .btn-create-map:hover {
        background: #f0fdfa;
        opacity: 1;
        color: #115e59;
    }

    .btn-create-map svg {
        width: 16px;
        height: 16px;
    }

    /* Field Label - Clean modern style */
    .field-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #111827;
        letter-spacing: -0.01em;
        flex: 1;
        line-height: 1.4;
    }

    /* Field Code - Subtle inline code */
    .field-code {
        font-size: 0.75rem;
        font-weight: 400;
        color: #9ca3af;
        font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
        background: transparent;
        padding: 0;
        border-radius: 0;
        flex-shrink: 0;
        border: none;
    }

    /* Field Value - Clean readable text */
    .field-value {
        font-size: 0.9375rem;
        color: #374151;
        line-height: 1.6;
        margin-top: 0.25rem;
    }


    .field-value a.media-link {
        color: #0f766e;
        font-weight: 500;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        transition: all 0.2s ease;
    }

    .field-value a.media-link:hover {
        color: #115e59;
        text-decoration: underline;
    }

    .field-value a.media-link svg {
        flex-shrink: 0;
    }

    /* Media Thumbnail */
    .media-thumbnail-container {
        display: inline-block;
        margin-top: 0.5rem;
    }

    .media-thumbnail {
        max-width: 200px;
        max-height: 200px;
        border-radius: 0.375rem;
        border: 2px solid #e7e5e4;
        cursor: pointer;
        transition: all 0.2s ease;
        display: block;
    }

    .media-thumbnail:hover {
        border-color: #0f766e;
        box-shadow: 0 4px 12px rgba(15, 118, 110, 0.15);
        transform: scale(1.02);
    }

    .media-caption {
        margin-top: 0.5rem;
        font-size: 0.875rem;
        color: #64748b;
        font-style: italic;
    }

    /* Image Viewer Modal */
    .image-viewer-modal .modal-dialog {
        max-width: 90vw;
    }

    .image-viewer-container {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 1rem;
        background: #f8fafc;
        border-radius: 0.5rem;
    }

    .full-image {
        max-width: 100%;
        max-height: 80vh;
        object-fit: contain;
        border-radius: 0.5rem;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }

    /* Answer Label Markers for Question Maps */
    .answer-label-marker {
        background: none !important;
        border: none !important;
        /* Center the label */
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }

    .answer-label {
        background: white;
        border: 2px solid #0f766e;
        border-radius: 0.375rem;
        padding: 0.375rem 0.625rem;
        font-size: 0.8125rem;
        font-weight: 600;
        color: #0f172a;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
        /* Position relative to center */
        position: relative;
        transform: translate(-50%, -50%);
        display: inline-block;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .answer-label:hover {
        background: #f0fdfa;
        border-color: #115e59;
        box-shadow: 0 4px 12px rgba(15, 118, 110, 0.25);
        transform: translate(-50%, -50%) scale(1.05);
        z-index: 1000;
    }
    </style>